import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TENANT_ME_QUERY_KEY } from '@/lib/api/tenants';
import { resyncStoreOrders } from '@/lib/api/stores';
import {
  getSyncConfig,
  patchSyncConfig,
  SYNC_CONFIG_QUERY_KEY,
  type SyncConfig,
  type SyncMode,
} from '@/lib/api/sync-config';

// Mirrors the constant in `apps/web/src/features/orders/OrdersPage.tsx`.
// Setting it before navigating to /app/orders lets OrdersPage's mount
// effect re-attach the SyncJobModal to the freshly-created resync job
// without a second hop.
const ACTIVE_JOB_STORAGE_KEY = 'mailchute.active-sync-job-id';

/**
 * Sync configuration section nested inside the BigCommerce store card
 * on `/app/stores`. Reads the connection's current mode + trigger
 * statuses + the live status list via `GET /sync-config`, lets the
 * merchant change either, and PATCHes the result. The PATCH endpoint
 * (de)registers BC webhooks per ADR 0018; the toast surfaces
 * success/failure but the heavy lifting is server-side.
 *
 * Returns fragments (CardContent + CardFooter) so the parent StoreCard
 * stitches them into a single visual surface with the store details
 * above. Statuses are the filter regardless of mode — sync mode picks
 * *when* Mailchute looks; the status ticks pick *which* orders count.
 */
const MODE_LABELS: Record<SyncMode, string> = {
  status: 'Status-triggered (recommended)',
  automatic: 'Automatic — every order',
  manual: 'Manual — only on demand',
};

const MODE_DESCRIPTIONS: Record<SyncMode, string> = {
  status:
    "Pull orders the moment their BigCommerce status changes to one of the ones you've ticked below.",
  automatic:
    "Pull orders the moment they're created or updated in BigCommerce. Only the ones whose status matches the ticks below come through.",
  manual:
    'Only pull when you click "Sync now" on the Orders page, and even then only the orders whose status matches the ticks below.',
};

interface SyncConfigSectionProps {
  readonly connectionId: string;
}

export function SyncConfigSection({ connectionId }: SyncConfigSectionProps) {
  const configQuery = useQuery({
    queryKey: SYNC_CONFIG_QUERY_KEY(connectionId),
    queryFn: async () => {
      const result = await getSyncConfig(connectionId);
      if (result.isErr()) throw new Error(result.error.message);
      return result.value;
    },
  });

  return (
    <>
      <div className="border-border border-t px-6 pt-6">
        <h3 className="text-foreground text-base font-semibold">Sync configuration</h3>
        <p className="text-muted-foreground text-sm">
          How Mailchute pulls orders from this BigCommerce store.
        </p>
      </div>
      {configQuery.data ? (
        <SyncConfigForm connectionId={connectionId} initial={configQuery.data} />
      ) : configQuery.isError ? (
        <CardContent>
          <p className="text-muted-foreground text-sm">
            We couldn&apos;t load the sync configuration. Please retry.
          </p>
        </CardContent>
      ) : (
        <SyncConfigSkeleton />
      )}
    </>
  );
}

/**
 * Form-shaped skeleton matching SyncConfigForm's layout: a Select-sized
 * rectangle for the mode picker, a checkbox-grid block for the status
 * list. Per [ADR 0021](../../../../docs/adr/0021-frontend-feedback-loop-polish.md)
 * — skeletons mirror eventual content so the swap is jank-free. Six
 * placeholder rows roughly match the live BC status set (Incomplete,
 * Pending, Awaiting Payment, Awaiting Fulfillment, Awaiting Shipment,
 * Shipped).
 */
function SyncConfigSkeleton() {
  return (
    <CardContent role="status" aria-label="Loading sync configuration" className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-9 w-full sm:w-[280px]" />
        <Skeleton className="h-4 w-3/4" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-full max-w-md" />
        <div className="border-border bg-card grid gap-2 rounded-md border p-3 sm:grid-cols-2">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={`status-${String(i)}`} className="flex items-center gap-2">
              <Skeleton className="size-4 rounded" />
              <Skeleton className="h-4 w-32" />
            </div>
          ))}
        </div>
      </div>
    </CardContent>
  );
}

function SyncConfigForm({ connectionId, initial }: { connectionId: string; initial: SyncConfig }) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<SyncMode>(initial.sync_mode);
  const [statuses, setStatuses] = useState<readonly number[]>(initial.sync_trigger_statuses);

  // Re-sync local state when the server data changes (e.g. another tab).
  useEffect(() => {
    setMode(initial.sync_mode);
    setStatuses(initial.sync_trigger_statuses);
  }, [initial.sync_mode, initial.sync_trigger_statuses]);

  const mutation = useMutation({
    mutationFn: async () => {
      const result = await patchSyncConfig(connectionId, {
        sync_mode: mode,
        sync_trigger_statuses: [...statuses],
      });
      if (result.isErr()) throw new Error(result.error.message);
      return result.value;
    },
    onSuccess: () => {
      toast.success('Sync configuration saved.');
      void queryClient.invalidateQueries({ queryKey: SYNC_CONFIG_QUERY_KEY(connectionId) });
      void queryClient.invalidateQueries({ queryKey: TENANT_ME_QUERY_KEY });
    },
    onError: () => {
      toast.error("We couldn't save the sync configuration. Please try again.");
    },
  });

  const sortedStatuses = [...initial.available_statuses].sort((a, b) => a.id - b.id);
  const dirty =
    mode !== initial.sync_mode || !sameNumberSet(statuses, initial.sync_trigger_statuses);
  // Statuses gate ingest regardless of mode (the worker filters every
  // order against this set), so we always require at least one.
  const statusesInvalid = statuses.length === 0;
  const canSave = dirty && !statusesInvalid && !mutation.isPending;

  function toggleStatus(id: number, checked: boolean) {
    setStatuses((prev) =>
      checked ? [...new Set([...prev, id])].sort((a, b) => a - b) : prev.filter((x) => x !== id),
    );
  }

  function handleReset() {
    setMode(initial.sync_mode);
    setStatuses(initial.sync_trigger_statuses);
  }

  return (
    <>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="sync-mode">Sync mode</Label>
          <Select
            value={mode}
            onValueChange={(next) => {
              setMode(next as SyncMode);
            }}
          >
            <SelectTrigger id="sync-mode" className="w-full sm:w-[280px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(['status', 'automatic', 'manual'] as const).map((m) => (
                <SelectItem key={m} value={m}>
                  {MODE_LABELS[m]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-muted-foreground text-sm">{MODE_DESCRIPTIONS[mode]}</p>
        </div>

        <div className="space-y-2">
          <Label>Order statuses</Label>
          <p className="text-muted-foreground text-sm">
            Mailchute only ingests orders whose BigCommerce status is in this set. The mode above
            picks <em>when</em> we look; the ticks below pick <em>which</em> orders count.
          </p>
          <div className="border-border bg-card grid gap-2 rounded-md border p-3 sm:grid-cols-2">
            {sortedStatuses.map((s) => {
              const checkboxId = `status-${String(s.id)}`;
              const checked = statuses.includes(s.id);
              return (
                <div key={s.id} className="flex items-center gap-2">
                  <Checkbox
                    id={checkboxId}
                    checked={checked}
                    onCheckedChange={(next) => {
                      toggleStatus(s.id, next === true);
                    }}
                  />
                  <Label htmlFor={checkboxId} className="text-sm font-normal">
                    {s.name}
                    <span className="text-muted-foreground ml-1 tabular-nums">#{s.id}</span>
                  </Label>
                </div>
              );
            })}
          </div>
          {statusesInvalid ? (
            <p className="text-destructive text-sm">Tick at least one status to continue.</p>
          ) : null}
        </div>
      </CardContent>
      <CardFooter className="border-border justify-end gap-2 border-t pt-6">
        <Button type="button" variant="outline" onClick={handleReset} disabled={!dirty}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={() => {
            mutation.mutate();
          }}
          disabled={!canSave}
        >
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
      </CardFooter>
      {initial.sync_mode === 'manual' ? <ResyncAllSection connectionId={connectionId} /> : null}
    </>
  );
}

/**
 * "Resync all orders" maintenance action — manual-mode only escape
 * hatch for re-ingesting the full BC history (e.g. after a Mailchute
 * schema change adds a column that needs back-population). Shown only
 * when the *saved* mode is manual; automatic + status modes have BC
 * webhooks doing the keeping-up and would race the live stream.
 *
 * UX: destructive-variant button + a confirmation Dialog. On confirm,
 * POSTs `/stores/{id}/resync` (which clears `sync_cursor` server-side
 * and enqueues a no-`since` SyncJob), stashes the returned job_id in
 * the same localStorage slot OrdersPage's modal reads from, and
 * navigates to `/app/orders` — the SyncJobModal re-attaches on mount.
 */
function ResyncAllSection({ connectionId }: { connectionId: string }) {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      const result = await resyncStoreOrders(connectionId);
      if (result.isErr()) throw new Error(result.error.message);
      return result.value;
    },
    onSuccess: (data) => {
      globalThis.localStorage.setItem(ACTIVE_JOB_STORAGE_KEY, data.job_id);
      toast.success('Resync started. Watch progress on the Orders page.');
      setConfirmOpen(false);
      void navigate('/app/orders');
    },
    onError: () => {
      toast.error("We couldn't start the resync. Please try again.");
    },
  });

  return (
    <>
      <div className="border-border border-t px-6 pt-6">
        <h3 className="text-foreground text-base font-semibold">Resync all orders</h3>
        <p className="text-muted-foreground text-sm">
          Pull every order from BigCommerce again from the beginning of history. Useful after a
          schema change or if your store and Mailchute look out of sync. May take several minutes.
        </p>
      </div>
      <CardFooter className="justify-end pt-6">
        <Button
          type="button"
          variant="destructive"
          onClick={() => {
            setConfirmOpen(true);
          }}
        >
          Resync all orders
        </Button>
      </CardFooter>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resync all orders?</DialogTitle>
            <DialogDescription>
              This re-pulls every order from BigCommerce from the beginning of history. The current
              orders list stays visible; new rows appear as the job processes them. May take several
              minutes for large stores.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setConfirmOpen(false);
              }}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                mutation.mutate();
              }}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Starting…' : 'Yes, resync all'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function sameNumberSet(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  return b.every((x) => setA.has(x));
}
